import React, { useState } from 'react';
import { Award, GraduationCap, Briefcase, ShieldCheck } from 'lucide-react';
import { Credential } from '../types';

interface CredentialsProps {
  credentials: Credential[];
}

export const Credentials: React.FC<CredentialsProps> = ({ credentials }) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredCredentials = activeCategory
    ? credentials.filter((c) => c.category === activeCategory)
    : credentials;

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'education':
        return <GraduationCap size={18} color="var(--accent-gold)" />;
      case 'certification':
        return <ShieldCheck size={18} color="var(--accent-emerald)" />;
      case 'experience':
        return <Briefcase size={18} color="var(--accent-cyan)" />;
      case 'award':
        return <Award size={18} color="var(--accent-gold)" />;
      default:
        return <Award size={18} />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'experience':
        return 'Professional Experience';
      case 'education':
        return 'Education';
      case 'certification':
        return 'Certifications';
      case 'award':
        return 'Honors & Festivals';
      default:
        return category;
    }
  };

  const categories = [
    { id: null, label: `All Background (${credentials.length})` },
    { id: 'experience', label: 'Experience' },
    { id: 'education', label: 'Education' },
    { id: 'certification', label: 'Certifications' },
    { id: 'award', label: 'Honors & Festivals' },
  ];

  return (
    <section id="credentials" className="credentials-section">
      <div className="container">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <span className="section-tag">Credentials & Background</span>
            <h2 className="section-title">Experience & Education</h2>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {categories.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.label}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`btn ${isActive ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '0.4rem 1rem', fontSize: '0.85rem' }}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="credentials-grid">
          {filteredCredentials.map((c, index) => (
            <div key={index} className="credential-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span className="credential-category">{getCategoryLabel(c.category)}</span>
                {getCategoryIcon(c.category)}
              </div>

              <h3 className="credential-title">{c.title}</h3>
              <p className="credential-institution">
                {c.institution} &bull; <span style={{ color: 'var(--text-muted)' }}>{c.period}</span>
              </p>
              {c.details && (
                <p className="credential-details">{c.details}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
